import { Component, OnDestroy, OnInit, ViewChild, ElementRef, Input, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KeycloakService } from 'keycloak-angular';
import { WrapperStreamService } from '../../shared-services/wrapper-stream.service';
import { Subscription } from 'rxjs';
import { MacroTemplateService } from '../../shared-services/macro-template.service';

interface Message {
  text: string;
  sender: 'user' | 'agent';
  formattedText?: string;
  files?: File[];
  isStreaming?: boolean;
  loading?: boolean;
}

@Component({
  selector: 'app-wrapper-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './wrapper-chat.component.html',
  styleUrls: ['./chat.component.scss', './wrapper-chat.component.scss']
})
export class WrapperChatComponent implements OnInit, OnDestroy {
  @ViewChild('chatBody') private chatBody!: ElementRef;
  @ViewChild('chatInput') private chatInput?: ElementRef<HTMLTextAreaElement>;

  @Input() agentName: string = 'Agent';
  @Input() agentFilePath: string = '';
  @Input() greeting: string = '';

  messages: Message[] = [];
  currentMessage: string = '';
  isLoading: boolean = false;
  selectedFiles: File[] = [];
  userAvatarLabel: string = 'U';

  private currentAbort: AbortController | null = null;
  private macroTemplateSubscription: Subscription | null = null;
  private readonly minInputHeightPx = 56;
  private readonly maxInputHeightPx = 180;

  constructor(
    private wrapperStream: WrapperStreamService,
    private macroTemplateService: MacroTemplateService,
    @Optional() private keycloakService?: KeycloakService
  ) {}

  get agentAvatarLabel(): string {
    const value = (this.agentName || 'A').trim();
    return value.length > 0 ? value.charAt(0).toUpperCase() : 'A';
  }

  ngOnInit(): void {
    if (this.greeting && this.messages.length === 0) {
      this.messages.push({
        sender: 'agent',
        text: this.greeting,
        formattedText: this.formatText(this.greeting),
        isStreaming: false
      });
    }

    void this.loadUserAvatarLabel();
    this.macroTemplateSubscription = this.macroTemplateService.templateSelected$.subscribe((template: string) => {
      if (!this.isChatAgentTab()) {
        return;
      }
      this.currentMessage = template;
      setTimeout(() => {
        this.autoResizeChatInput();
        this.chatInput?.nativeElement.focus();
      }, 0);
    });

    // Ensure the textarea is sized correctly on first render.
    setTimeout(() => this.autoResizeChatInput(), 0);
  }

  ngOnDestroy(): void {
    this.currentAbort?.abort();
    this.macroTemplateSubscription?.unsubscribe();
    this.macroTemplateSubscription = null;
  }

  startNewSession(): void {
    this.currentAbort?.abort();
    this.currentAbort = null;
    this.messages = [];
    this.isLoading = false;
    this.selectedFiles = [];
    if (this.greeting) {
      this.messages.push({
        sender: 'agent',
        text: this.greeting,
        formattedText: this.formatText(this.greeting),
        isStreaming: false
      });
    }
    this.scrollToBottom();
    setTimeout(() => this.autoResizeChatInput(), 0);
  }

  sendMessageOnEnter(event: Event | KeyboardEvent): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (!fileList) {
      return;
    }
    for (let i = 0; i < fileList.length; i++) {
      this.selectedFiles.push(fileList[i]);
    }
  }

  removeFile(fileToRemove: File): void {
    this.selectedFiles = this.selectedFiles.filter(f => f !== fileToRemove);
  }

  sendMessage(): void {
    if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
      return;
    }
    if (!this.agentFilePath) {
      this.messages.push({
        sender: 'agent',
        text: 'Error: agentFilePath not configured for this tab.',
        formattedText: 'Error: agentFilePath not configured for this tab.',
        isStreaming: false
      });
      return;
    }

    // Abort any in-flight request if the user sends a new message.
    this.currentAbort?.abort();
    this.currentAbort = new AbortController();

    const userText = this.currentMessage.trim();
    const files = [...this.selectedFiles];

    this.messages.push({
      sender: 'user',
      text: userText,
      formattedText: this.formatText(userText),
      files,
      isStreaming: false
    });

    this.currentMessage = '';
    this.selectedFiles = [];
    this.isLoading = true;
    setTimeout(() => this.autoResizeChatInput(), 0);

    const agentMessage: Message = {
      sender: 'agent',
      text: '',
      formattedText: '',
      isStreaming: true
    };
    agentMessage.loading = true;
    this.messages.push(agentMessage);
    this.scrollToBottom();

    void this.wrapperStream.stream(
      this.agentFilePath,
      userText,
      {
        onMeta: (data: any) => {
          // Keep the typing indicator visible until the first chunk arrives.
          // This also provides a hook if we want to display PID/runDir in the UI later.
          void data;
        },
        onChunk: (data: any) => {
          const delta = typeof data?.delta === 'string' ? data.delta : '';
          if (!delta) {
            return;
          }
          if (agentMessage.loading) {
            agentMessage.loading = false;
          }
          agentMessage.text += delta;
          agentMessage.formattedText = this.formatText(agentMessage.text);
          this.scrollToBottom();
        },
        onDone: (data: any) => {
          const outputText = this.extractWrapperOutputText(data);
          const errorText = this.extractWrapperErrorText(data);

          if (errorText && !outputText) {
            agentMessage.text = errorText;
          } else if (outputText) {
            agentMessage.text = outputText;
          }
          agentMessage.formattedText = this.formatText(agentMessage.text);
          agentMessage.isStreaming = false;
          agentMessage.loading = false;
          this.isLoading = false;
          this.scrollToBottom();
        },
        onError: (data: any) => {
          const msg = typeof data?.error === 'string' ? data.error : 'Streaming error';
          agentMessage.text = msg;
          agentMessage.formattedText = msg;
          agentMessage.isStreaming = false;
          agentMessage.loading = false;
          this.isLoading = false;
          this.scrollToBottom();
        }
      },
      this.currentAbort.signal
    ).catch((err: any) => {
      const msg = err?.message ? String(err.message) : 'Streaming request failed';
      agentMessage.text = msg;
      agentMessage.formattedText = msg;
      agentMessage.isStreaming = false;
      agentMessage.loading = false;
      this.isLoading = false;
      this.scrollToBottom();
    });
  }

  autoResizeChatInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) {
      return;
    }

    // Reset height so scrollHeight reflects the full content height.
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, this.maxInputHeightPx);
    const clamped = Math.max(next, this.minInputHeightPx);

    el.style.height = `${clamped}px`;
    el.style.overflowY = el.scrollHeight > this.maxInputHeightPx ? 'auto' : 'hidden';
  }

  private isChatAgentTab(): boolean {
    const normalized = (this.agentFilePath || '').replace(/\\/g, '/').toLowerCase();
    return normalized.endsWith('chat_agent/chat_agent_cli.py');
  }

  private async loadUserAvatarLabel(): Promise<void> {
    if (!this.keycloakService) {
      return;
    }

    try {
      const loggedIn = await this.keycloakService.isLoggedIn();
      if (!loggedIn) {
        return;
      }

      const profile = await this.keycloakService.loadUserProfile();
      const fullName =
        `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() ||
        (profile?.username ?? '');
      this.userAvatarLabel = this.toInitials(fullName);
    } catch {
      // Keep default avatar label if user profile is unavailable.
      this.userAvatarLabel = 'U';
    }
  }

  private toInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return 'U';
    }
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }

  private extractWrapperOutputText(data: any): string {
    return this.textValue(data?.OUPTUT) || this.responseJsonValue(data?.RESPONSE_JSON, 'response');
  }

  private extractWrapperErrorText(data: any): string {
    return this.textValue(data?.ERROR) || this.responseJsonValue(data?.RESPONSE_JSON, 'error');
  }

  private textValue(value: any): string {
    if (Array.isArray(value)) {
      return value
        .map(item => this.textValue(item))
        .filter(Boolean)
        .join('\n');
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private responseJsonValue(value: any, key: 'response' | 'error'): string {
    const rawValue = Array.isArray(value) ? value.find(item => !!this.textValue(item)) : value;
    if (typeof rawValue !== 'string') {
      return this.textValue(rawValue?.[key]);
    }

    try {
      return this.textValue(JSON.parse(rawValue)?.[key]);
    } catch {
      return '';
    }
  }

  formatText(text: string): string {
    let formatted = text;
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/^-\s(.+)$/gm, '<li>$1</li>');
    formatted = formatted.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul>$1</ul>');
    return formatted;
  }

  scrollToBottom(): void {
    try {
      setTimeout(() => this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight, 0);
    } catch {
      // ignore
    }
  }
}
